import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type User } from '@prisma/client'
import {
	json,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Link, useSearchParams } from '@remix-run/react'
import {
	ArrowLeftIcon,
	ArrowRightCircle,
	Equal,
	ExternalLinkIcon,
	Hexagon,
	PlayCircleIcon,
	TreeDeciduous,
} from 'lucide-react'
import { GiftDemoModal } from '#app/components/dialogs/gift-dialog.js'
import { OwnDemoModal } from '#app/components/dialogs/own-dialog.js'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion'
import { Button } from '#app/components/ui/button'
import { getUserById } from '#app/models/user/get-user'
import { authenticator } from '#app/utils/auth.server'
import { isEarthDay } from '#app/utils/is-earth-day.js'
import { cn } from '#app/utils/misc'

type LoaderData = {
	user: User | null
}

export const handle: SEOHandle = {
	getSitemapEntries: () => [{ route: '/info' }],
}

export async function loader({ request }: LoaderFunctionArgs) {
	const session = await authenticator.isAuthenticated(request)

	if (!session) return json<LoaderData>({ user: null })

	const user = await getUserById(session.id)

	return json<LoaderData>({ user })
}

export const meta: MetaFunction = () => [
	{ title: `Info, Guides & FAQs | Erda` },
	{
		name: 'description',
		content: `What is Erda? How do I own or git Plots? How are trees planted? Find answers to these questions and more here.`,
	},
	{
		name: 'keywords',
		content:
			'info, faq, frequently asked questions, about, questions, help, how to, guide',
	},
	{ name: 'og:title', content: `Info, Guides & FAQs | Erda` },
	{ name: 'og:type', content: 'website' },
	{ name: 'og:url', content: 'https://erda.app' },
	{
		name: 'og:image',
		content: `https://erda.app/image/info.png`,
	},
	{ name: 'og:card', content: 'summary_large_image' },
	{ name: 'og:creator', content: '@erda_app' },
	{ name: 'og:site', content: 'https://erda.app' },
	{
		name: 'og:description',
		content: `What is Erda? How do I own or git Plots? How are trees planted? Find answers to these questions and more here.`,
	},
	{
		name: 'twitter:image',
		content: `https://erda.app/image/info.png`,
	},
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:creator', content: '@erda_app' },
	{ name: 'twitter:title', content: `Info, Guides & FAQs | Erda` },
	{
		name: 'twitter:description',
		content: `What is Erda? How do I own or git Plots? How are trees planted? Find answers to these questions and more here.`,
	},
]

export default function Info() {
	const [searchParams] = useSearchParams()
	const from = searchParams.get('from')
	const fromLabel = from ? from.charAt(1).toUpperCase() + from.slice(2) : null

	return (
		<>
			{from && (
				<Button
					data-vaul-no-drag
					variant="link"
					className="h-auto w-fit self-center"
					asChild
				>
					<Link to={from}>
						<ArrowLeftIcon className="h-4 w-4" /> {fromLabel}
					</Link>
				</Button>
			)}
			<div>
				<h1 className="mx-auto w-[280px] text-center text-sm font-bold tracking-tight md:w-[360px] md:text-lg">
					Own or Gift digital Plots — each a piece of the world meaningful to
					you, for just $2 per year.
				</h1>

				<div className="my-1 flex items-center justify-center gap-2 rounded-lg p-4">
					<Hexagon
						className={cn('h-8 w-8 fill-purple-400/50 stroke-purple-500')}
						strokeWidth={1.5}
					/>
					<Equal className="h-6 w-6" />
					<div className="flex items-center justify-center">
						<TreeDeciduous
							className="h-8 w-8 fill-green-400/50 stroke-green-500"
							strokeWidth={1.5}
						/>
						<TreeDeciduous
							className="h-8 w-8 fill-green-400/50 stroke-green-500"
							strokeWidth={1.5}
						/>
					</div>
				</div>
				<p className="mx-auto w-[280px] text-center text-sm tracking-tight md:w-[350px] md:text-base">
					Every purchase and renewal{' '}
					<span className="text-sm font-bold md:text-lg">
						plants{' '}
						{isEarthDay() ? (
							<>
								<span className="text-muted-foreground line-through">two</span>{' '}
								three trees
							</>
						) : (
							'two trees'
						)}
					</span>
					, helping break the cycle of hunger, poverty, and environmental
					destruction.{' '}
					<a
						href="https://trees.org"
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center gap-1 underline"
					>
						How? <ExternalLinkIcon className="inline h-4 w-4" />
					</a>
				</p>
			</div>

			<hr className="mt-2" />

			<div className="mt-4 flex-1 space-y-2">
				<div className="flex items-baseline justify-between">
					<h2 className="font-bold">
						Claim something unique, make a difference
					</h2>
				</div>
				<p className="text-sm">
					Do you have a place that's meaningful to you? Somewhere you love,
					aspire to visit, wish to protect, or share with others?
				</p>
				<p className="text-sm">
					Own a digital Plot on the map, a unique piece of the world for you to
					own forever.
				</p>
				<p className="text-sm">
					In doing so, you'll also be planting{' '}
					{isEarthDay() ? (
						<>
							<span className="text-muted-foreground line-through">two</span>{' '}
							three trees
						</>
					) : (
						'two trees'
					)}
					, helping to break the cycle of hunger, poverty, and environmental
					destruction.
				</p>
			</div>

			<div className="mt-4 flex gap-3">
				<div className="flex flex-1 flex-col items-center">
					<Button
						variant="outline"
						asChild
						className="relative h-24 justify-start gap-3"
					>
						<Link to="/own-something-unique">
							<span className="absolute left-4 top-2 text-xs text-muted-foreground">
								How to...
							</span>
							<span className="text-lg font-semibold">Own Plots</span>
							<ArrowRightCircle className="h-6 w-6" />
						</Link>
					</Button>
					<OwnDemoModal
						trigger={
							<Button size="sm" variant="link" className="mt-2">
								<PlayCircleIcon className="h-4 w-4" /> Watch demo
							</Button>
						}
					/>
				</div>
				<div className="flex flex-1 flex-col items-center">
					<Button
						variant="outline"
						asChild
						className="relative h-24 flex-1 justify-start gap-3"
					>
						<Link to="/digital-gift">
							<span className="absolute left-4 top-2 text-xs text-muted-foreground">
								How to...
							</span>
							<span className="text-lg font-semibold">Gift Plots</span>
							<ArrowRightCircle className="h-6 w-6" />
						</Link>
					</Button>
					<GiftDemoModal
						trigger={
							<Button size="sm" variant="link" className="mt-2">
								<PlayCircleIcon className="h-4 w-4" /> Watch demo
							</Button>
						}
					/>
				</div>
			</div>
			<div className="mt-4 flex-1">
				<h2 className="font-bold">FAQs</h2>
				<Accordion type="single" collapsible className="w-full">
					<AccordionItem data-vaul-no-drag value="item-1">
						<AccordionTrigger data-vaul-no-drag>
							<h3 data-vaul-no-drag className="text-sm">
								What is a Plot?
							</h3>
						</AccordionTrigger>
						<AccordionContent>
							<p>A Plot is a digital piece of land on the map.</p>
						</AccordionContent>
					</AccordionItem>
					<AccordionItem data-vaul-no-drag value="item-3">
						<AccordionTrigger data-vaul-no-drag>
							<h3 data-vaul-no-drag className="text-sm">
								Why can't I select a Plot?
							</h3>
						</AccordionTrigger>
						<AccordionContent>
							<p>
								Ensure you're zoomed in enough to see the Plots. Purple Plots
								are already owned.
							</p>
						</AccordionContent>
					</AccordionItem>
					<AccordionItem
						data-vaul-no-drag
						value="item-9"
						className="border-b-0"
					>
						<AccordionTrigger data-vaul-no-drag>
							<h3 data-vaul-no-drag className="text-sm">
								How do you plant trees?
							</h3>
						</AccordionTrigger>
						<AccordionContent className="flex flex-col gap-2">
							<p>
								With each ownership and renewal, we donate to Trees for the
								Future (
								<a
									href="https://trees.org"
									target="_blank"
									rel="noreferrer"
									className="inline-flex items-center gap-1 underline"
								>
									https://trees.org
								</a>
								) to plant{' '}
								{isEarthDay() ? (
									<>
										<span className="text-muted-foreground line-through">
											two
										</span>{' '}
										three trees
									</>
								) : (
									'two trees'
								)}{' '}
								in developing communities. This donation helps break the cycle
								of hunger, poverty, and environmental destruction.
							</p>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</div>
			<div className="sticky -bottom-10 flex w-full flex-col  items-center justify-center gap-2 border-t bg-background pb-10 pt-3">
				<div className="flex gap-2">
					{/* TODO (med) page on tree planting / charity */}
					<Button data-vaul-no-drag variant="link" asChild>
						<Link to="/info">Info</Link>
					</Button>
					&bull;
					<Button data-vaul-no-drag variant="link" asChild>
						<Link to="/terms">Terms</Link>
					</Button>
					&bull;
					<Button data-vaul-no-drag variant="link" asChild>
						<Link to="/privacy">Privacy</Link>
					</Button>
					&bull;
					<Button data-vaul-no-drag variant="link" asChild>
						<Link to="/contact">Contact</Link>
					</Button>
				</div>
			</div>
		</>
	)
}
